import React, { ComponentType } from 'react';
import { Container, Row, Column, Justify } from '@uds/grid';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import PaddingBox from '@uds/padding-box';
import { Heading02, Heading04 } from '@uds/typography';
import Ribbon from 'components/ribbons/Ribbon';
import visibilitySet from 'presentation/hocs/visibilitySet';
import errorBoundary from 'presentation/hocs/errorBoundary';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { isNilOrEmpty, isNotNilNorEmpty } from 'presentation/utils/generalUtils';
import { CTA } from 'core/ribbons/cta';
import CallToAction from 'presentation/components/common/CallToAction';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { useBreakpoints } from '@ux/responsive';
import { Alignment } from 'domain/constants/alignment';
import responsiveColumns from 'presentation/utils/responsiveColumns';
import { ResponsiveSet } from 'core/ribbons/primitives';
import { ColumnCount } from 'domain/constants/layout';

import getClassNames, { StyledBody } from './FreeSpeech.stylesheet';

interface OuterProps extends RibbonProps {
  body: ShortcodeProps;
  title: ShortcodeProps;
  subtitle: ShortcodeProps;
  hBaseline: number;
  ctas?: Array<CTA>;
}

export const PureFreeSpeech = ({
  id,
  colorway,
  layout,
  loading,
  name,
  body,
  hBaseline = 1,
  title,
  subtitle,
  ctas,
}: OuterProps) => {
  const { columns } = layout;
  const hasCtas = isNotNilNorEmpty(ctas);
  const hasTitle = !isNilOrEmpty(title);
  const hasSubtitle = !isNilOrEmpty(subtitle);

  const alignment = findResponsiveItem(useBreakpoints(), [
    layout?.alignmentMobile ?? Alignment.STARTING,
    layout?.alignmentTablet ?? Alignment.STARTING,
    layout?.alignment ?? Alignment.STARTING,
  ]);

  const classNames = getClassNames({
    alignment,
    hasMultipleCtas: hasCtas && ctas.length > 1,
  });

  const columnCount = responsiveColumns([1, 1, 1], columns as ResponsiveSet<ColumnCount>);

  return (
    <Ribbon id={id} colorway={colorway} loading={loading} layout={layout} name={name} type={Type.FREE_SPEECH}>
      <Container>
        <PaddingBox>
          <Row justify={Justify.CENTER}>
            <Column xs={12}>
              {(hasTitle || hasSubtitle) && (
                <header className={classNames.header}>
                  {hasTitle && (
                    <Heading02 depth={hBaseline}>
                      <Shortcode code={title} against={colorway} />
                    </Heading02>
                  )}
                  {hasSubtitle && (
                    <Heading04 depth={hBaseline + 1} className={classNames.subtitle}>
                      <Shortcode code={subtitle} against={colorway} />
                    </Heading04>
                  )}
                </header>
              )}
              <StyledBody className={classNames.body} columnCount={columnCount as [number, number, number]}>
                <Shortcode code={body} against={colorway} />
              </StyledBody>
            </Column>
          </Row>
          {ctas != null && (
            <div className={classNames.ctas}>
              {ctas.map((cta, i) => {
                return (
                  <div
                    className={classNames.cta}
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                  >
                    <CallToAction key={(id ?? '').concat(String(i))} ribbonId={id} index={i} {...cta} />
                  </div>
                );
              })}
            </div>
          )}
        </PaddingBox>
      </Container>
    </Ribbon>
  );
};

PureFreeSpeech.displayName = 'FreeSpeech';

export default visibilitySet(errorBoundary(PureFreeSpeech)) as ComponentType<OuterProps>;
